import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import {useQuery} from "@tanstack/react-query";
import corporationService from "../../../../../../services/web/corporation-service.js";
import {APIProvider, ControlPosition, Map as GoogleMaps, MapControl, Marker, Polyline, useMap, useMapsLibrary, Circle} from "@vis.gl/react-google-maps";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import prepareAddress from "../utils/prepareAddress.js";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import Button from "../../../../../../components/button/button.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import {FaRotate} from "react-icons/fa6";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const ROUTE_DASH_SYMBOL = {
    path: "M 0,-1 0,1",
    strokeOpacity: 1,
    scale: 3,
};

const SosMapMarkers = ({citizen, aggressorLocation, eventPosition, target, actionRef, routePoints, initialPoint, showRoute}) => {
    const map = useMap();
    const coreLib = useMapsLibrary("core");

    const [modal, setModal] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [address, setAddress] = useState(undefined);

    const markers = useRef(new Map());
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (!coreLib || !map) return;

        const bounds = new coreLib.LatLngBounds();
        bounds.extend(new coreLib.LatLng(eventPosition.lat, eventPosition.lng));

        const newAddress = [];
        citizen?.addresses?.forEach((address) => {
            address.title = "Endereço do cidadão";
            newAddress.push(address);
            bounds.extend(new coreLib.LatLng(address.latitude, address.longitude));
        });

        citizen?.protectedPlaces?.forEach((address) => {
            address.isProtectedPlace = true;
            address.title = "Local protegido";

            newAddress.push(address);
            bounds.extend(new coreLib.LatLng(address.latitude, address.longitude));
        });

        citizen?.aggressors?.forEach((aggressor) => aggressor?.addresses?.forEach(address => {
            address.isAggressorAddress = true
            address.title = "Endereço do agressor - " + aggressor.name
            newAddress.push(address);
            bounds.extend(new coreLib.LatLng(address.latitude, address.longitude));
        }));

        [...aggressorLocation].forEach(([, marker])=> {
            bounds.extend(new coreLib.LatLng(marker.latitude, marker.longitude));
        })

        setAddresses(newAddress);

        map.fitBounds(bounds);
    }, [coreLib, map]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!map || !coreLib) return;

        if (showRoute && routePoints?.length > 1) {
            const bounds = new coreLib.LatLngBounds();
            routePoints.forEach(point => bounds.extend(new coreLib.LatLng(Number(point.latitude), Number(point.longitude))));
            map.fitBounds(bounds);
            return;
        }

        map.panTo(eventPosition);
    }, [showRoute]);

    const addressMarker = (address) => {
        if (!address.latitude || !address.longitude) return;

        const myLatLng = { lat: address.latitude, lng: address.longitude };

        let id = address.id;

        if (address.isProtectedPlace) {
            id = address.placeId ? address.placeId : address.id;
        }

        let icon = "img/sos/map-markers/pin-address.png";
        if (address.isAggressorAddress) icon = "img/sos/map-markers/pin-address-grey.png"
        if (!target && address.isProtectedPlace) icon = "img/sos/map-markers/pin-address-red.png"

        return (
            <Marker
                key={id}
                ref={marker => { markers.current.set("address-" + address.id, marker) }}
                position={myLatLng}
                title={address.description ? address.description : prepareAddress(address)}
                zIndex={1}
                icon={getAssetPath(icon)}
                onClick={() => {
                    setAddress(address);
                    setModal(true);
                }}
            />
        )
    }

    useImperativeHandle(actionRef, () => ({
        click: (target) => {
            const marker = markers.current.get(target);
            if (marker) {
                map.setCenter(marker.getPosition());
            }
        }
    }));

    return (
        <>
            <Marker
                ref={marker => { markers.current.set("citizen", marker) }}
                position={eventPosition}
                title={"Última localização"}
                zIndex={3}
                icon={getAssetPath(target === "citizen" ? "img/sos/map-markers/pin-person-red.png" : "img/sos/map-markers/pin-person.png")}
                onClick={(e) => map.setCenter(e.latLng)}
            />

            {
                showRoute && routePoints?.length > 1 &&
                <>
                    <Marker
                        position={{lat: Number(initialPoint.latitude), lng: Number(initialPoint.longitude)}}
                        title={"Localização do acionamento"}
                        zIndex={3}
                        icon={{
                            url: getAssetPath(target === "citizen" ? "img/sos/map-markers/pin-person-location-red.svg" : "img/sos/map-markers/pin-person-location.svg"),
                            scaledSize: new google.maps.Size(42, 42)
                        }}
                        onClick={(e) => map.setCenter(e.latLng)}
                    />

                    <Polyline
                        path={routePoints.map(point => ({lat: Number(point.latitude), lng: Number(point.longitude)}))}
                        strokeColor={"#123a5e"}
                        strokeOpacity={0}
                        icons={[{icon: ROUTE_DASH_SYMBOL, offset: "0", repeat: "14px"}]}
                    />
                </>
            }

            {
                addresses.map((address) => addressMarker(address))
            }

            {
                [...aggressorLocation].map(([itemId, marker], index) => {
                    const aggressor = citizen?.aggressors.find(a => a.itemId === itemId);
                    const circleColors = ['#4572A7', '#AA4643', '#4198AF', '#DB843D', '#93A9CF'];

                    return (
                        <React.Fragment key={"location-aggressor-" + itemId}>
                            <Marker
                                ref={marker => { markers.current.set("aggressor-" + itemId, marker) }}
                                position={{lat: marker.latitude, lng: marker.longitude}}
                                title={"Última localização do agressor"}
                                zIndex={2}
                                icon={getAssetPath("img/sos/map-markers/pin-person.png")}
                                onClick={(e) => map.setCenter(e.latLng)}
                            />

                            {
                                !!aggressor && aggressor?.minDistance &&
                                <Circle
                                    center={{lat: marker.latitude, lng: marker.longitude}}
                                    radius={parseInt(aggressor?.minDistance)}
                                    strokeOpacity={0.5}
                                    strokeWeight={2}
                                    fillOpacity={0.35}
                                    strokeColor={circleColors[index % circleColors.length]}
                                    fillColor={circleColors[index % circleColors.length]}
                                />
                            }
                        </React.Fragment>
                    )
                })
            }

            <Modal open={modal} setOpen={setModal} title={address?.title} overflow={true} className={"w-6/12"}>
                <Modal.Body className={"grid grid-cols-12 gap-3"}>
                    <div className="col-span-12">
                        <div className={"h-[137px]"}>
                            <div
                                className={"cursor-pointer bg-no-repeat bg-contain h-full border border-[#e0e0e0] p-1 rounded"}
                                style={{backgroundImage: address?.attachId ? `url(${API_KEEPER}/ged/attach/${address.attachId})` : `url(${getAssetPath("img/no-img.png")})`, backgroundPosition: "50% center"}}
                            />
                        </div>
                    </div>

                    <InputForm
                        id={"addressType"}
                        label={"Tipo"}
                        classNameWrapper={"col-span-3"}
                        value={address?.type}
                        disabled
                    />
                    <InputForm
                        id={"addressStreet"}
                        label={"Logradouro"}
                        classNameWrapper={"col-span-7"}
                        value={address?.address}
                        disabled
                    />
                    <InputForm
                        id={"addressNumber"}
                        label={"Número"}
                        classNameWrapper={"col-span-2"}
                        value={address?.number}
                        disabled
                    />
                    <InputForm
                        id={"addressNeighborhood"}
                        label={"Bairro"}
                        classNameWrapper={"col-span-4"}
                        value={address?.neighborhood}
                        disabled
                    />
                    <InputForm
                        id={"addressComplement"}
                        label={"Complemento"}
                        classNameWrapper={"col-span-4"}
                        value={address?.complement}
                        disabled
                    />
                    <InputForm
                        id={"addressCity"}
                        label={"Município"}
                        classNameWrapper={"col-span-4"}
                        value={address?.city}
                        disabled
                    />
                    <InputForm
                        id={"addressZipCode"}
                        label={"CEP"}
                        classNameWrapper={"col-span-3"}
                        value={address?.zipCode}
                        disabled
                    />
                    <InputForm
                        id={"addressDescription"}
                        label={"Identificação"}
                        classNameWrapper={"col-span-9"}
                        value={address?.description}
                        disabled
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button target="secondary" onClick={() => setModal(false)}>
                        Ok
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

const Reset = ({eventPosition, citizen, aggressorLocation, routePoints, showRoute}) => {
    const map = useMap();
    const coreLib = useMapsLibrary("core");

    const resetMap = useCallback(() => {
        if (!coreLib || !map) return;

        const bounds = new coreLib.LatLngBounds();
        bounds.extend(new coreLib.LatLng(eventPosition.lat, eventPosition.lng));

        citizen?.addresses?.forEach((address) => {
            bounds.extend(new coreLib.LatLng(address.latitude, address.longitude));
        });

        citizen?.protectedPlaces?.forEach((address) => {
            bounds.extend(new coreLib.LatLng(address.latitude, address.longitude));
        });

        citizen?.aggressors?.forEach((aggressor) => aggressor.addresses?.forEach(address => {
            bounds.extend(new coreLib.LatLng(address.latitude, address.longitude));
        }));

        [...aggressorLocation].forEach(([, marker])=> {
            bounds.extend(new coreLib.LatLng(marker.latitude, marker.longitude));
        })

        if (showRoute) {
            routePoints?.forEach(point => bounds.extend(new coreLib.LatLng(Number(point.latitude), Number(point.longitude))));
        }

        map.fitBounds(bounds);
    }, [map, coreLib, eventPosition, aggressorLocation, routePoints, showRoute]);

    return (
        <MapControl position={ControlPosition.RIGHT_BOTTOM}>
            <div className="print-none p-[10px]">
                <button className={"w-[40px] h-[40px] shadow-md rounded-full bg-white flex items-center justify-center cursor-pointer"} onClick={() => resetMap()}>
                    <FaRotate size={20}/>
                </button>
            </div>
        </MapControl>
    )
}

const SosMap = ({ref, location, routePoints, initialPoint, showRoute, aggressorLocation, target, citizen, defaultHeight, actionRef}) => {
    const {data: corporation, isSuccess} = useQuery({
        queryFn: () => corporationService.entity(),
        queryKey: ["corporation"]
    });

    const parsedLatitude = parseFloat(location?.latitude);
    const parsedLongitude = parseFloat(location?.longitude);
    const hasValidCoordinates = isFinite(parsedLatitude) && isFinite(parsedLongitude);

    const fallbackLatitude = parseFloat(corporation?.latitude) || 0;
    const fallbackLongitude = parseFloat(corporation?.longitude) || 0;

    const position = {
        lat: hasValidCoordinates ? parsedLatitude : fallbackLatitude,
        lng: hasValidCoordinates ? parsedLongitude : fallbackLongitude
    }

    return (
        <Card className={defaultHeight}>
            <Card.Body className={"h-full"} ref={ref}>
                {
                    !isSuccess &&
                    <div className="animate-pulse h-full">
                        <div className="h-full bg-gray-200"></div>
                    </div>
                }

                {
                    isSuccess &&
                    <APIProvider apiKey={corporation?.maps_api_key ?? ""}>
                        <GoogleMaps
                            reuseMaps={true}
                            defaultZoom={12}
                            streetViewControl={false}
                            defaultCenter={position}
                            gestureHandling={'greedy'}
                            style={{ height: "100%", width: "100%" }}
                            mapTypeControlOptions={{position: ControlPosition.TOP_RIGHT}}
                            fullscreenControlOptions={{position: ControlPosition.BOTTOM_RIGHT}}
                        >
                            <Reset
                                citizen={citizen}
                                aggressorLocation={aggressorLocation}
                                eventPosition={{lat: Number(location?.latitude) || 0, lng: Number(location?.longitude) || 0}}
                                routePoints={routePoints}
                                showRoute={showRoute}
                            />

                            {
                                location?.latitude && location?.longitude &&
                                <SosMapMarkers
                                    citizen={citizen}
                                    aggressorLocation={aggressorLocation}
                                    eventPosition={{lat: Number(location?.latitude) || 0, lng: Number(location?.longitude) || 0}}
                                    target={target}
                                    actionRef={actionRef}
                                    routePoints={routePoints}
                                    initialPoint={initialPoint}
                                    showRoute={showRoute}
                                />
                            }
                        </GoogleMaps>
                    </APIProvider>
                }
            </Card.Body>
        </Card>
    );
};

export default SosMap;